import { Box, Button, Flex, FormField, InputTextField, Row } from '@tempmee/react';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useUpdateUser } from '~/domain/UpdateUser';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireContactInfoSubmitted } from '~/posthogs/events/Signup';
import { routes } from '~/routes';
import { formatPhoneForDisplay, formatPhoneOnChange } from '~/utils/formatPhoneOnChange';
import { AuthErrorBanner, useAuthFormSubmit } from '../../shared';
import type { ISignUpFormValues } from '../types';
import { MfaModal } from './MfaModal';

const FORM_WIDTH = 704;
const NAME_MAX_LENGTH = 50;
const NAME_REGEX = /^[\p{L} '\-.]+$/u;
const CTA_WIDTH = 296;

const FIELD_ORDER: Array<keyof Pick<ISignUpFormValues, 'firstName' | 'lastName' | 'mobileNumber'>> =
  ['firstName', 'lastName', 'mobileNumber'];

interface IUserLike {
  first_name?: string | null;
  last_name?: string | null;
  contact_number?: string | null;
}

export const YourInfoForm = () => {
  const navigate = useNavigate();
  const user = useUserSelector() as IUserLike | null | undefined;
  const { mutateAsync: updateUser, isPending } = useUpdateUser();
  const [mfaPhone, setMfaPhone] = useState<string | null>(null);

  const {
    control,
    getValues,
    reset,
    setValue,
    formState: { dirtyFields, errors },
  } = useFormContext<ISignUpFormValues>();

  const { submit, bannerVisible, bannerRef, serverMessage } = useAuthFormSubmit<ISignUpFormValues>({
    onValid: async ({ firstName, lastName, mobileNumber }) => {
      fireContactInfoSubmitted();
      if (dirtyFields.firstName || dirtyFields.lastName) {
        await updateUser({ firstName, lastName });
      }
      // Snapshot the saved values as the new clean baseline so subsequent
      // submits with the same values don't re-trigger the mutate.
      reset(getValues());
      if (!dirtyFields.mobileNumber) {
        navigate(routes.signUpRedesign.officeDetails);
        return;
      }
      const e164 = parsePhoneNumber(mobileNumber, 'US').number;
      const currentE164 = user?.contact_number
        ? parsePhoneNumber(user.contact_number, 'US')?.number
        : null;
      if (e164 === currentE164) {
        navigate(routes.signUpRedesign.officeDetails);
        return;
      }
      setMfaPhone(e164);
    },
  });

  useEffect(() => {
    if (!user) {
      navigate(routes.signUpRedesign.signUp, { replace: true });
      return;
    }
    if (user.first_name) {
      setValue('firstName', user.first_name);
      setValue('lastName', user.last_name ?? '');
      setValue('mobileNumber', formatPhoneForDisplay(user.contact_number ?? ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldErrorMessage = FIELD_ORDER.map((key) => errors[key])
    .filter((err): err is NonNullable<typeof err> => Boolean(err) && err?.type !== 'required')
    .map((err) => err.message as string | undefined)
    .find(Boolean);
  const bannerMessage = serverMessage ?? fieldErrorMessage;

  return (
    <Box as="div" style={{ width: FORM_WIDTH }}>
      <form onSubmit={submit} data-testid="your-info-form" noValidate>
        <Box
          ref={bannerRef}
          style={{ marginTop: bannerVisible ? -16 : 0, marginBottom: bannerVisible ? 8 : 0 }}
        >
          <AuthErrorBanner visible={bannerVisible} message={bannerMessage} />
        </Box>

        <Row gap={4} align="start" wrap="nowrap">
          <Box style={{ flex: 1 }}>
            <FormField label="First Name" htmlFor="firstName">
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: 'First name is required',
                  maxLength: { value: NAME_MAX_LENGTH, message: 'Maximum 50 characters' },
                  pattern: {
                    value: NAME_REGEX,
                    message: 'Use letters, spaces, hyphens, apostrophes, or periods only',
                  },
                }}
                render={({ field, fieldState }) => (
                  <InputTextField
                    data-cy="input-first-name"
                    id="firstName"
                    placeholder="Enter your first name"
                    isError={Boolean(fieldState.error)}
                    {...field}
                  />
                )}
              />
            </FormField>
          </Box>

          <Box style={{ flex: 1 }}>
            <FormField label="Last Name" htmlFor="lastName">
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: 'Last name is required',
                  maxLength: { value: NAME_MAX_LENGTH, message: 'Maximum 50 characters' },
                  pattern: {
                    value: NAME_REGEX,
                    message: 'Use letters, spaces, hyphens, apostrophes, or periods only',
                  },
                }}
                render={({ field, fieldState }) => (
                  <InputTextField
                    data-cy="input-last-name"
                    id="lastName"
                    placeholder="Enter your last name"
                    isError={Boolean(fieldState.error)}
                    {...field}
                  />
                )}
              />
            </FormField>
          </Box>
        </Row>

        <Box style={{ marginTop: 24, width: '100%' }}>
          <FormField label="Mobile Number" htmlFor="mobileNumber">
            <Controller
              name="mobileNumber"
              control={control}
              rules={{
                required: 'Mobile number is required',
                validate: (value) =>
                  isValidPhoneNumber(value, 'US') || 'Enter a valid US mobile number',
              }}
              render={({ field, fieldState }) => (
                <InputTextField
                  data-cy="input-mobile-number"
                  id="mobileNumber"
                  type="tel"
                  placeholder="Enter your mobile number"
                  isError={Boolean(fieldState.error)}
                  value={field.value}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  onChange={(e) =>
                    field.onChange(formatPhoneOnChange(field.value ?? '', e.target.value))
                  }
                />
              )}
            />
          </FormField>
        </Box>

        <Flex justify="center" style={{ marginTop: 48 }}>
          <Box style={{ width: CTA_WIDTH }}>
            <Button
              data-cy="btn-your-info-continue"
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isPending}
              style={{ width: '100%' }}
            >
              Tell Us About Your Office
            </Button>
          </Box>
        </Flex>
      </form>
      <MfaModal
        open={mfaPhone !== null}
        phone={mfaPhone ?? ''}
        onClose={() => setMfaPhone(null)}
        onVerified={() => {
          setMfaPhone(null);
          navigate(routes.signUpRedesign.officeDetails);
        }}
      />
    </Box>
  );
};
